import { Input as AntInput, type InputRef } from 'antd';
import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import {
  InputClearAction,
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

const AntPassword = AntInput.Password;

export type SpiiInputProps = Partial<{
  value: string;
  defaultValue: string;
  placeholder: string;
  disabled: boolean;
  readOnly: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  className: string;
  id: string;
  name: string;
  'aria-labelledby': string;
  autoComplete: string;
  status: 'error' | 'default';
  label: string;
  optional: boolean;
  infoIcon: boolean;
  infoText: string;
  helpText: string;
  clearAction: boolean;
  onClear: () => void;
}>;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntPassword = forwardRef<InputRef, any>((props, ref) => (
  <AntPassword {...props} ref={ref} />
));

const StyledInput = styled(ForwardedAntPassword)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
}>`
  &&.ant-input-password {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0 ${theme.dime.spacing.l};
    height: ${theme.dime.spacing['3xl']};
    box-shadow: none;
    margin-bottom: ${theme.dime.spacing.m};
  }

  &&.ant-input-password .ant-input {
    cursor: ${({ $disabled, $readOnly }) =>
      $disabled || $readOnly ? 'not-allowed' : 'text'};
    background-color: transparent;
  }

  &&.ant-input-password .ant-input:disabled::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-input-password .ant-input-suffix {
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  }
`;

export const SpiiInput = forwardRef<InputRef, SpiiInputProps>(
  (
    {
      value,
      defaultValue,
      placeholder = '',
      disabled = false,
      readOnly = false,
      onChange,
      onFocus,
      onBlur,
      className = '',
      id,
      name,
      'aria-labelledby': ariaLabelledBy,
      autoComplete = 'off',
      status = 'default',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      clearAction = false,
      onClear,
      ...rest
    },
    ref
  ) => {
    const hasError = status === 'error';
    const isIconInactive = disabled;
    const iconStyle = isIconInactive
      ? undefined
      : { color: theme.dime.colors.icon.primary };
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;

    const renderVisibilityIcon = (visible: boolean) => (
      <IconWrap
        type={visible ? IconType.Hide : IconType.Show}
        size={IconSize.Small}
        disabled={isIconInactive}
        style={iconStyle}
      />
    );

    return (
      <InputWrapper className={className}>
        {!!label && (
          <InputLabel
            id={labelId}
            label={label}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledInput
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          id={inputId}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          name={name}
          autoComplete={autoComplete}
          iconRender={renderVisibilityIcon}
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
        {!!clearAction && !disabled && !readOnly && (
          <InputClearAction onClear={onClear} />
        )}
      </InputWrapper>
    );
  }
);

SpiiInput.displayName = 'SpiiInput';
